import { motion } from "framer-motion";
import { computeConnectionScore, connectionVibeLabel } from "../lib/connectionScore";

const BARS = [
  { key: "chemistry", label: "Chemistry", color: "#FF4D8D", emoji: "💕" },
  { key: "chaos",     label: "Chaos",     color: "#FFB547", emoji: "🔥" },
  { key: "soulmate",  label: "Soulmate",  color: "#7B61FF", emoji: "💫" },
  { key: "toxic",     label: "Toxic",     color: "#00E5FF", emoji: "🌶️" },
];

/**
 * ConnectionScore — 4 animated percentage bars + vibe label.
 * Expects the same props ScoreCard already knows.
 */
export default function ConnectionScore({
  hostScore = 0,
  guestScore = 0,
  roundsPlayed = 0,
  spiceLevel,
  modeLabel,
  compact = false,
}) {
  const scores = computeConnectionScore({
    hostScore,
    guestScore,
    roundsPlayed,
    spiceLevel,
    modeLabel,
  });
  const label = connectionVibeLabel(scores);

  return (
    <div
      className="relative rounded-2xl border border-white/10 p-5"
      style={{
        background:
          "radial-gradient(120% 120% at 0% 0%, rgba(255,77,141,0.10), transparent 50%), radial-gradient(120% 120% at 100% 100%, rgba(123,97,255,0.10), transparent 50%), rgba(15,15,20,0.6)",
      }}
      data-testid="connection-score-card"
    >
      {!compact && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#C9C7D9]">
            Connection read
          </p>
          <p className="font-display italic text-sm text-white/90">
            "{label}"
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {BARS.map((bar, i) => (
          <Bar key={bar.key} {...bar} value={scores[bar.key]} delay={i * 0.12} />
        ))}
      </div>
    </div>
  );
}

function Bar({ label, color, emoji, value, delay }) {
  return (
    <div data-testid={`connection-bar-${label.toLowerCase()}`}>
      <div className="flex items-end justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#C9C7D9]">
          {emoji} {label}
        </span>
        <motion.span
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: delay + 0.25, duration: 0.45 }}
          className="font-display-tight font-bold text-lg text-white tabular-nums"
          style={{ color }}
        >
          {value}%
        </motion.span>
      </div>
      <div className="relative h-2 rounded-full bg-white/5 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            background: `linear-gradient(90deg, ${color} 0%, ${color}AA 100%)`,
            boxShadow: `0 0 12px ${color}66`,
          }}
        />
      </div>
    </div>
  );
}
